import React, { useEffect, useMemo, useRef, useState } from 'react';
import { get, isEmpty } from 'lodash';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';
import { isCountry } from 'helpers/is-country';

const getPlacesPostCodeById = async (placeId) =>
  new Promise((resolve, reject) => {
    if (!placeId) reject('placeId not provided');

    try {
      new window.google.maps.places.PlacesService(
        document.createElement('div'),
      ).getDetails(
        {
          placeId,
          fields: ['address_components'],
        },
        (details) => {
          let postcode = null;
          details?.address_components?.forEach((entry) => {
            if (entry.types?.[0] === 'postal_code') {
              postcode = entry.long_name;
            }
          });
          return resolve(postcode);
        },
      );
    } catch (e) {
      reject(e);
    }
  });

function loadScript(src, position, id) {
  if (!position) {
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('async', '');
  script.setAttribute('id', id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService = { current: null };

const useStyles = makeStyles((theme) => ({
  icon: {
    marginRight: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}));

export default function GooglePlacesAutocomplete({
  address,
  name,
  setFieldValue,
  error,
  touched,
  helperText,
  required,
  className,
}) {
  const classes = useStyles();
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const loaded = useRef(false);
  const [postcode, setPostcode] = useState(null);

  if (typeof window !== 'undefined' && !loaded.current) {
    if (!document.querySelector('#google-maps')) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_PLACES_API_KEY}&libraries=places`,
        document.querySelector('head'),
        'google-maps',
      );
    }

    console.log(
      'GOOGLE PLACES API KEY: ',
      process.env.REACT_APP_GOOGLE_PLACES_API_KEY,
    );

    loaded.current = true;
  }

  const fetch = useMemo(
    () =>
      throttle((request, callback) => {
        autocompleteService.current.getPlacePredictions(request, callback);
      }, 200),
    [],
  );

  useEffect(() => {
    if (value) {
      const { terms } = value;

      if (isCountry(terms[3].value)) {
        setFieldValue('city', terms[1].value);
        setFieldValue('state', terms[2].value);
        setFieldValue('country', terms[3].value);
      } else {
        setFieldValue('city', terms[2].value);
        setFieldValue('state', terms[3].value);
        setFieldValue('country', terms[4].value);
      }
    }
  }, [value]);

  useEffect(async () => {
    let active = true;

    if (!autocompleteService.current && window.google) {
      console.log('window.google', window.google);
      console.log('window.google.maps.places', window.google.maps.places);
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch(
      {
        input: inputValue,
        componentRestrictions: { country: ['us', 'ca'] },
        types: ['address'],
      },
      (results) => {
        if (active) {
          let newOptions = [];

          if (value) {
            newOptions = [value];
          }

          if (results) {
            newOptions = [...newOptions, ...results];
          }

          setOptions(newOptions);
        }
      },
    );

    const { place_id } = value || {};

    if (place_id) {
      try {
        const postcode = await getPlacesPostCodeById(place_id);
        if (isEmpty(postcode)) {
          setFieldValue('postalCode', '');
        } else {
          setFieldValue('postalCode', postcode);
        }
      } catch (e) {
        console.error(e);
      }
    }

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  return (
    <Autocomplete
      className={className}
      getOptionLabel={(option) => option.structured_formatting.main_text}
      getOptionLabel={(option) =>
        typeof option === 'string'
          ? option
          : option.structured_formatting.main_text
      }
      autoComplete={false}
      filterOptions={(x) => x}
      options={options}
      includeInputInList
      filterSelectedOptions
      value={address}
      freeSolo
      onBlur={(event) => setFieldValue('address', event.target.value)}
      onChange={(event, newValue) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Address"
          variant="outlined"
          fullWidth
          error={error}
          helperText={helperText}
          required={required}
        />
      )}
      renderOption={(option) => {
        const matches =
          option.structured_formatting.main_text_matched_substrings;
        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match) => [match.offset, match.offset + match.length]),
        );

        return (
          <Grid container alignItems="center">
            <Grid item>
              <LocationOnIcon className={classes.icon} />
            </Grid>
            <Grid item xs>
              {parts.map((part, index) => (
                <span
                  key={index}
                  style={{ fontWeight: part.highlight ? 700 : 400 }}
                >
                  {part.text}
                </span>
              ))}

              <Typography variant="body2" color="textSecondary">
                {option.structured_formatting.secondary_text}
              </Typography>
            </Grid>
          </Grid>
        );
      }}
    />
  );
}
